import React, { useState, useEffect, useRef } from "react";
import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Divider,
  CloseButton,
  FieldWrapper,
  Input,
  ErrorMessage,
  DragAndDropLinkField,
} from "./CreateModal.styles";
import { CreateModalProps } from "./CreateModal.types";
import Button from "../../Buttons/Button";
import DragAndDropField from "../../DragAndDropField/DragAndDropField";
import { closeIcon as CloseIcon } from "assets/icons";
import ToggleSwitch from "../../ToggleSwitch/ToggleSwitch";
import DropdownWithInput from "../../Dropdowns/DropdownWithInput";
import ReactDOM from "react-dom";
import DropdownWithObjects from "../../Dropdowns/DropdownWithObjects/DropdownWithObjects";
import DatePickerInput from "../../CalendarInput/CalendarInput";
import TimePickerInput from "../../TimePickerInput/TimePickerInput";

import { logger } from "utils/logger";
const CreateModal: React.FC<CreateModalProps> = ({
  title,
  fields,
  onSubmit,
  submitButtonText = "Создать курс",
  hasDragAndDrop = false,
  onCancel,
  onRemoveFile,
  initialValues = {},
  files_from_storage = [],
  disableToggleSwitch = false,
  maxFiles = 5,
  externalErrors = {},
  isLoading = false,
}) => {
  logger.log('CreateModal: isLoading =', isLoading);
  const [formData, setFormData] = useState<Record<string, string>>(
    fields.reduce((acc, field) => {
      acc[field.name] = initialValues[field.name] || field.value || "";
      return acc;
    }, {} as Record<string, string>)
  );

  const [fileData, setFileData] = useState<File[] | null>(
    Array.isArray(initialValues.files) ? (initialValues.files as File[]) : null
  );
  const [linkData, setLinkData] = useState<string>(initialValues.link || "");
  const [errors, setErrors] = useState<Record<string, string | null>>(externalErrors || {});
  const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(true);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [activeTab, setActiveTab] = useState<string>("Файл");

  // Удаляем захардкоженное ограничение - теперь DragAndDropField получает его с бэкенда
  // const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20 МБ

  useEffect(() => {
    const allRequiredFieldsFilled = fields.every(
      (field) =>
        !field.required ||
        (field.name !== "files" &&
          field.name !== "link" &&
          formData[field.name]?.trim() !== "") ||
        (activeTab === "Файл" && fileData && fileData.length > 0) ||
        (activeTab === "Ссылка" && linkData.trim() !== "")
    );
    setIsSubmitDisabled(!allRequiredFieldsFilled);
  }, [formData, fields, activeTab, fileData, linkData]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
    setLinkData(initialValues.video_url || "");
  }, []);

  useEffect(() => {
    setErrors((prevErrors) => {
      if (JSON.stringify(prevErrors) !== JSON.stringify(externalErrors)) {
        return {
          ...prevErrors,
          ...externalErrors,
        };
      }
      return prevErrors;
    });
  }, [externalErrors]);

  const handleChange = (name: string, value: string) => {
    setFormData({ ...formData, [name]: value });
    logger.log(formData);
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const handleTextareaChange = (name: string, value: string) => {
    setFormData({ ...formData, [name]: value });

    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }

    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const handleLinkChange = (value: string) => {
    setLinkData(value);
    if (errors["link"]) {
      setErrors({ ...errors, link: null });
    }
  };

  const handleToggleTab = (option: string) => {
    if (option === "Файл" && !fileData) {
      setFileData([]);
    }
    setActiveTab(option);
  };

  const handleSubmit = () => {
    const newErrors = fields.reduce<Record<string, string | null>>((acc, field) => {
      if (field.required && !formData[field.name]?.trim() && !(field.name === "files" || field.name === "link")) {
        acc[field.name] = "Это поле обязательно";
      }
      // // Для полей "files" или "link" требуем, чтобы хотя бы одно из них было заполнено
      // if ((field.name === "files" || field.name === "link") && !((fileData && fileData.length > 0) || linkData.trim() !== "")) {
      //   acc[field.name] = "Необходимо указать хотя бы файл или ссылку";
      // }
      return acc;
    }, {});

    setErrors(newErrors);

    if (Object.values(newErrors).every((err) => !err)) {
      const dataToSubmit = {
        ...formData,
        // Передаем оба значения независимо от активной вкладки
        files: fileData,
        video_url: linkData,
      };
      onSubmit(dataToSubmit);
    }
  };


  const modalContent = (
    <ModalOverlay>
      <ModalContent>
        <ModalHeader>
          <h2>{title}</h2>
          <CloseButton aria-label="Закрыть" onClick={onCancel}>
            <CloseIcon aria-hidden="true" />
          </CloseButton>
        </ModalHeader>
        <Divider />
        <ModalBody>
          <form onSubmit={(e) => e.preventDefault()}>
            {fields.map(
              (field) =>
                (!hasDragAndDrop || field.name !== "link") && (
                  <FieldWrapper key={field.name}>
                    {errors[field.name] && <ErrorMessage>{errors[field.name]}</ErrorMessage>}
                    {field.type === "date" ? (
                      <DatePickerInput
                        value={formData[field.name] || ""}
                        placeholder={field.placeholder || ""}
                        onChange={(value: string) => handleChange(field.name, value)}
                      />
                    ) : field.type === "time" ? (
                      <TimePickerInput
                        value={formData[field.name] || ""}
                        placeholder={field.placeholder || ""}
                        onChange={(value: string) => handleChange(field.name, value)}
                      />
                    ) : field.type === "textarea" ? (
                      <Input
                        id={field.name}
                        as="textarea"
                        ref={textareaRef}
                        placeholder={field.placeholder}
                        value={formData[field.name]}
                        onChange={(e) => handleTextareaChange(field.name, e.target.value)}
                        hasError={!!errors[field.name]}
                      />
                    ) : field.type === "selectWithInput" && field.options ? (
                      Array.isArray(field.options) &&
                      (typeof field.options[0] === "object" ? (
                        <DropdownWithObjects
                          options={field.options as { label: string; value: string }[]}
                          placeholder={field.placeholder || "Выберите значение"}
                          value={formData[field.name]}
                          onChange={(value) => handleChange(field.name, value)}
                        />
                      ) : (
                        <DropdownWithInput
                          options={field.options as string[]}
                          placeholder={field.placeholder || "Выберите значение"}
                          value={formData[field.name]}
                          onChange={(value) => handleChange(field.name, value)}
                        />
                      ))
                    ) : (
                      <Input
                        type={field.type}
                        min={field.type === "number" ? 1 : undefined}
                        placeholder={field.placeholder}
                        value={formData[field.name]}
                        onChange={(e) => handleChange(field.name, e.target.value)}
                        hasError={!!errors[field.name]}
                      />
                    )}
                  </FieldWrapper>
                )
            )}

            {hasDragAndDrop && (
              <DragAndDropLinkField>
                {!disableToggleSwitch && (
                  <ToggleSwitch
                    options={["Ссылка", "Файл"]}
                    activeOption={activeTab}
                    onToggle={handleToggleTab}
                  />
                )}
                {/* Файл всегда рендерится, просто скрываем его, если активна вкладка "Ссылка" */}
                <div style={{ display: activeTab === "Файл" ? "block" : "none" }}>
                  <DragAndDropField
                    files={fileData}
                    onFileSelected={setFileData}
                    placeholder="Перетащите файлы сюда или нажмите, чтобы выбрать."
                    maxFiles={maxFiles}
                    // maxFileSize теперь получается с бэкенда автоматически
                    files_from_storage={files_from_storage}
                    onRemoveFile={onRemoveFile}
                    key={files_from_storage?.map(f => `${f.id}-${f.progress_percent}`).join(',') || 'empty'}
                  />
                </div>
                {/* Ссылка всегда рендерится, просто скрываем ее, если активна вкладка "Файл" */}
                <div style={{ display: activeTab === "Ссылка" ? "block" : "none" }}>
                  <FieldWrapper className="link-field">
                    {errors["video_url"] && <ErrorMessage>{errors["video_url"]}</ErrorMessage>}
                    <Input
                      type="text"
                      placeholder="Введите ссылку"
                      value={linkData}
                      onChange={(e) => handleLinkChange(e.target.value)}
                      hasError={!!errors["link"]}
                    />
                  </FieldWrapper>
                </div>
              </DragAndDropLinkField>
            )}

          </form>
        </ModalBody>
        <ModalFooter>
          <Button 
            type="button" 
            disabled={isSubmitDisabled || isLoading}  
            onClick={handleSubmit}
            fullWidth
          >
            {isLoading ? "Сохранение..." : submitButtonText}
          </Button>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default CreateModal;
